import { useQueryClient } from '@tanstack/react-query';
import {
  addTemplateToPointLabelStyle,
  createPointLabelStyle,
  deletePointLabelStyle,
  getPointLabelStyle,
  removeTemplateFromPointLabelStyle,
  searchPointLabelStyles,
  updatePointLabelStyle,
} from '../../api/point-label-styles.api';
import { SearchPointLabelStylesApiRequest } from '../../../../types/requests/point-label-styles/search-point-label-styles.request';
import { useAxiosInfiniteQuery, useAxiosMutation, useAxiosQuery } from './use-axios.hook';
import { CreatePointLabelStyleApiRequest } from '../../../../types/requests/point-label-styles/create-point-label-style.request';
import { UpdatePointLabelStyleApiRequest } from '../../../../types/requests/point-label-styles/update-point-label-style.request';
import { PointLabelStyleApiModel } from '../../../../types/models/point-label-style.model';
import { GetPointLabelStyleApiRequest } from '../../../../types/requests/point-label-styles/get-point-label-style.request';
import { DeletePointLabelStyleApiRequest } from '../../../../types/requests/point-label-styles/delete-point-label-style.request';
import { AddTemplateToPointLabelStyleApiRequest } from '../../../../types/requests/point-label-styles/add-template-to-point-label-style.request';
import { RemoveTemplateFromPointLabelStyleApiRequest } from '../../../../types/requests/point-label-styles/remove-template-from-point-label-style.request';

const baseQueryKey = 'point-label-style';
const searchQueryKey = 'search';

export const useUpdatePointLabelStyle = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: UpdatePointLabelStyleApiRequest) => (await updatePointLabelStyle(params)).data,
    onSuccess: (pointLabelStyle) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey &&
          (queryKey[1] === 'search' ||
            (queryKey[1] as GetPointLabelStyleApiRequest)?.pointLabelStyleId === pointLabelStyle.id),
      }),
  });
  return [request, { ...result, pointLabelStyle: data }] as const;
};

export const useGetPointLabelStyle = <T extends PointLabelStyleApiModel>(params: GetPointLabelStyleApiRequest) => {
  const { data, ...result } = useAxiosQuery<T>({
    queryKey: [baseQueryKey, params],
    queryFn: async () => (await getPointLabelStyle<T>(params)).data,
    enabled: Boolean(params.pointLabelStyleId && !Number.isNaN(params.pointLabelStyleId)),
  });
  return { ...result, pointLabelStyle: data } as const;
};

export const useCreatePointLabelStyle = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: CreatePointLabelStyleApiRequest) => (await createPointLabelStyle(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, { ...result, pointLabelStyle: data }] as const;
};

export const useSearchPointLabelStyles = (params: SearchPointLabelStylesApiRequest) => {
  const { data, ...result } = useAxiosInfiniteQuery({
    queryKey: [baseQueryKey, searchQueryKey, params],
    queryFn: async ({ pageParam }) => (await searchPointLabelStyles({ cursor: pageParam, ...params })).data,
    initialPageParam: params.cursor,
    getNextPageParam: (lastResponse) => lastResponse.nextCursor,
  });
  return { ...result, response: data } as const;
};

export const useDeletePointLabelStyle = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: DeletePointLabelStyleApiRequest) => (await deletePointLabelStyle(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, result] as const;
};

export const useAddTemplateToPointLabelStyle = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: AddTemplateToPointLabelStyleApiRequest) =>
      (await addTemplateToPointLabelStyle(params)).data,
    onSuccess: (_, params) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey &&
          (queryKey[1] as GetPointLabelStyleApiRequest)?.pointLabelStyleId === params.pointLabelStyleId,
      }),
  });
  return [request, result] as const;
};

export const useRemoveTemplateFromPointLabelStyle = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: RemoveTemplateFromPointLabelStyleApiRequest) =>
      (await removeTemplateFromPointLabelStyle(params)).data,
    onSuccess: (_, params) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey &&
          (queryKey[1] as GetPointLabelStyleApiRequest)?.pointLabelStyleId === params.pointLabelStyleId,
      }),
  });
  return [request, result] as const;
};
